import { ar } from "./locales/ar/index.js";
import { en } from "./locales/en/index.js";

export const SUPPORTED_LOCALES = ["en", "ar"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

const RTL_LOCALES: ReadonlySet<string> = new Set(["ar"]);

type Catalog = Record<string, unknown>;

const CATALOGS: Record<string, Catalog> = {
  en: en as unknown as Catalog,
  ar: ar as unknown as Catalog,
};

export function isSupportedLocale(locale: string): locale is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(locale);
}

export function isRtl(locale: string): boolean {
  return RTL_LOCALES.has(locale);
}

export function getIntlLocale(locale: string): string {
  return locale === "ar" ? "ar-EG" : "en-US";
}

function resolve(catalog: Catalog, dottedPath: string): unknown {
  return dottedPath.split(".").reduce<unknown>((acc, segment) => {
    if (acc && typeof acc === "object" && segment in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[segment];
    }
    return undefined;
  }, catalog);
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) =>
    key in vars ? String(vars[key]) : `{{${key}}}`,
  );
}

/**
 * Resolve a dotted key like "emails.resetPassword.subject" for a locale.
 * Falls back to the default locale (en), then to the raw key if missing.
 */
export function t(
  locale: string,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const primary = isSupportedLocale(locale) ? CATALOGS[locale] : CATALOGS[DEFAULT_LOCALE];
  const fallback = CATALOGS[DEFAULT_LOCALE];

  const value = resolve(primary, key) ?? resolve(fallback, key);
  if (typeof value === "string") return interpolate(value, vars);
  return key;
}
